import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import toast from 'react-hot-toast';
import {
  Users, ShoppingBag, Gem, TrendingUp, Plus, Repeat, Eye, Share2, Copy, Key, MapPin,
} from 'lucide-react';
import { api, compact, money, timeAgo } from '../lib/api';
import { Avatar, TierBadge, SkeletonList, SectionHead, Sheet, Empty } from '../components/ui';

export default function ShopDashboard() {
  const { id } = useParams();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ username: '', amount: '', currency: '', reference: '', referral_code: '' });

  const { data, isLoading } = useQuery({ queryKey: ['shopDash', id], queryFn: () => api.get(`/shops/${id}/dashboard`).then(r => r.data) });

  const purchaseM = useMutation({
    mutationFn: () => api.post('/purchases', { ...f, shop_id: id, currency: f.currency || data.shop.currency }),
    onSuccess: ({ data: r }) => {
      toast.success(`✅ ${r.buyer.username} earned ${r.totalPoints} points (${r.multiplier.toFixed(2)}× · Tier ${r.tier})`, { duration: 6000 });
      setF({ username: '', amount: '', currency: '', reference: '', referral_code: '' });
      setOpen(false); qc.invalidateQueries(['shopDash', id]);
    },
    onError: e => toast.error(e.message),
  });

  if (isLoading || !data) return <SkeletonList n={4} h="h-32" />;
  const { shop, kpi, daily, tierDist, topCustomers, recent, cities, repeatRate, loyaltyTiers } = data;

  const chartData = daily.map(d => ({
    d: new Date(d.d).toLocaleDateString('en', { day: '2-digit', month: 'short' }),
    revenue: Number(d.revenue), orders: d.orders, points: Number(d.points),
  }));

  return (
    <div className="space-y-4 animate-fade-up">
      <div className="card p-4 flex items-center gap-3">
        <Avatar src={shop.logo_url} name={shop.name} size={48} square />
        <div className="flex-1 min-w-0">
          <h1 className="font-display text-lg font-extrabold truncate">{shop.name}</h1>
          <p className="text-xs text-ink-400 font-semibold">Business dashboard · {shop.currency}</p>
        </div>
        <Link to={`/shop/${shop.slug}`} className="btn-ghost btn-sm">View page</Link>
      </div>

      <button onClick={() => setOpen(true)} className="btn-primary w-full py-3.5 text-base font-black shadow-xl">
        <Plus size={18} /> Register a purchase
      </button>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Kpi icon={Users} label="Community" value={compact(kpi.customers)} sub={`${repeatRate}% return`} color="bg-blue-500" />
        <Kpi icon={ShoppingBag} label="Orders" value={compact(kpi.orders)} sub={`${kpi.orders_30d} last 30d`} color="bg-mint-500" />
        <Kpi icon={TrendingUp} label="Revenue" value={money(kpi.revenue, shop.currency)} sub={money(kpi.revenue_30d, shop.currency) + ' 30d'} color="bg-brand-500" />
        <Kpi icon={Gem} label="Points given" value={compact(kpi.points_given)} sub={`${compact(kpi.followers)} followers`} color="bg-grape-500" />
      </div>

      <div className="grid grid-cols-3 gap-2">
        <MiniKpi icon={Eye} label="Post views" value={compact(kpi.views)} />
        <MiniKpi icon={Share2} label="Shares" value={compact(kpi.shares)} />
        <MiniKpi icon={Repeat} label="Referred orders" value={compact(kpi.referred_orders)} />
      </div>

      {chartData.length > 1 && (
        <div className="card p-4">
          <SectionHead title="Last 30 days" sub="Revenue & orders" />
          <ResponsiveContainer width="100%" height={190}>
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#f97316" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#f97316" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis dataKey="d" tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} width={38} />
              <Tooltip contentStyle={{ borderRadius: 12, border: '1px solid #e2e8f0', fontSize: 12 }} />
              <Area type="monotone" dataKey="revenue" stroke="#f97316" strokeWidth={2.5} fill="url(#g1)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-3">
        <div className="card p-4">
          <SectionHead title="Loyalty tiers" sub="Your customer distribution" />
          <div className="space-y-2">
            {loyaltyTiers.map(t => {
              const row = tierDist.find(x => x.tier === t.tier);
              const c = row?.c || 0;
              const pct = kpi.customers ? (c / kpi.customers) * 100 : 0;
              return (
                <div key={t.tier}>
                  <div className="flex justify-between items-center text-xs font-bold mb-1">
                    <TierBadge tier={t.tier} label={t.label} multiplier={t.multiplier} />
                    <span className="text-ink-500">{c} customers</span>
                  </div>
                  <div className="stat-bar"><div className="stat-fill bg-brand-500" style={{ width: `${pct}%` }} /></div>
                </div>);
            })}
          </div>
        </div>

        <div className="card p-4">
          <SectionHead title="Where they come from" />
          {!cities.length ? <p className="text-sm text-ink-400">No data yet.</p> : (
            <ResponsiveContainer width="100%" height={170}>
              <BarChart data={cities} layout="vertical">
                <XAxis type="number" hide />
                <YAxis type="category" dataKey="handle" width={70} tick={{ fontSize: 10, fill: '#64748b' }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ borderRadius: 12, fontSize: 12 }} />
                <Bar dataKey="customers" fill="#8b5cf6" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>)}
        </div>
      </div>

      <div className="card p-4">
        <SectionHead title="Top customers" sub="Your most loyal community" />
        <div className="space-y-1">
          {topCustomers.map((c, i) => (
            <Link key={c.username} to={`/u/${c.username}`} className="flex items-center gap-3 p-2 rounded-xl hover:bg-ink-50">
              <span className="w-5 text-center font-black text-xs text-ink-400">{i + 1}</span>
              <Avatar src={c.avatar_url} name={c.username} size={34} />
              <div className="flex-1 min-w-0">
                <p className="font-bold text-sm truncate">{c.display_name || c.username}</p>
                <p className="text-[11px] text-ink-400 font-semibold">
                  {c.purchases_count} orders · {timeAgo(c.last_purchase_at)}
                </p>
              </div>
              <div className="text-right">
                <p className="font-black text-sm">{money(c.total_spent, shop.currency)}</p>
                <TierBadge tier={c.tier} label="" multiplier={c.multiplier} />
              </div>
            </Link>))}
        </div>
      </div>

      <div className="card p-4">
        <SectionHead title="Recent purchases" />
        {!recent.length ? <Empty icon={ShoppingBag} title="No purchases yet" sub="Register your first sale above." />
          : <div className="space-y-1">
            {recent.map(p => (
              <div key={p.id} className="flex items-center gap-3 p-2 rounded-xl hover:bg-ink-50">
                <div className="w-9 h-9 rounded-xl bg-mint-100 text-mint-600 grid place-items-center font-black text-xs">
                  {p.multiplier_applied}×
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-sm truncate">@{p.username}</p>
                  <p className="text-[11px] text-ink-400 font-semibold">
                    {timeAgo(p.created_at)} {p.referrer_username && `· via @${p.referrer_username}`}
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-black text-sm">{money(p.amount, p.currency)}</p>
                  <p className="text-[11px] text-brand-600 font-black">+{compact(p.total_points)}💎</p>
                </div>
              </div>))}
          </div>}
      </div>

      <ApiKeyCard shopId={id} apiKey={shop.api_key} />

      <Sheet open={open} onClose={() => setOpen(false)} title="Register a purchase">
        <div className="space-y-3">
          <div><label className="label">Customer username / email</label>
            <input className="input" placeholder="e.g. andrei" value={f.username}
              onChange={e => setF({ ...f, username: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="label">Amount</label>
              <input className="input" type="number" step="0.01" placeholder="100"
                value={f.amount} onChange={e => setF({ ...f, amount: e.target.value })} /></div>
            <div><label className="label">Currency</label>
              <select className="input" value={f.currency || shop.currency} onChange={e => setF({ ...f, currency: e.target.value })}>
                {['RON', 'EUR', 'USD', 'GBP'].map(c => <option key={c}>{c}</option>)}
              </select></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="label">Receipt no. (optional)</label>
              <input className="input" value={f.reference} onChange={e => setF({ ...f, reference: e.target.value })} /></div>
            <div><label className="label">Referral code (optional)</label>
              <input className="input uppercase" value={f.referral_code}
                onChange={e => setF({ ...f, referral_code: e.target.value.toUpperCase() })} /></div>
          </div>
          {f.amount > 0 && (
            <div className="p-3 rounded-xl bg-brand-50 text-sm font-bold text-brand-700">
              💎 Customer earns ≈ {Math.floor(Number(f.amount))} base points (×their loyalty multiplier)
            </div>)}
          <button onClick={() => purchaseM.mutate()} disabled={purchaseM.isPending || !f.username || !f.amount}
            className="btn-primary w-full py-3">
            {purchaseM.isPending ? 'Registering…' : 'Confirm & award points'}
          </button>
        </div>
      </Sheet>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, sub, color }) {
  return (
    <div className="card p-3">
      <div className={`w-8 h-8 rounded-lg ${color} grid place-items-center text-white mb-2`}><Icon size={15} /></div>
      <p className="text-[10px] font-black text-ink-400 uppercase">{label}</p>
      <p className="font-black text-lg leading-tight">{value}</p>
      {sub && <p className="text-[10px] text-ink-400 font-semibold">{sub}</p>}
    </div>
  );
}
function MiniKpi({ icon: Icon, label, value }) {
  return (
    <div className="card p-2.5 text-center">
      <Icon size={14} className="mx-auto text-ink-400 mb-1" />
      <p className="font-black text-sm">{value}</p>
      <p className="text-[9px] font-bold text-ink-400 uppercase">{label}</p>
    </div>
  );
}

function ApiKeyCard({ shopId, apiKey }) {
  const [show, setShow] = useState(false);
  const qc = useQueryClient();
  const rotate = useMutation({
    mutationFn: () => api.post(`/shops/${shopId}/rotate-key`),
    onSuccess: () => { toast.success('API key rotated'); qc.invalidateQueries(['shopDash', shopId]); },
  });
  return (
    <div className="card p-4">
      <SectionHead title="POS / API integration" sub="Auto-register purchases from your system" />
      <div className="flex gap-2">
        <input readOnly className="input font-mono text-xs" value={show ? apiKey : '•'.repeat(28)} />
        <button onClick={() => setShow(v => !v)} className="btn-ghost btn-sm">{show ? 'Hide' : 'Show'}</button>
        <button onClick={() => { navigator.clipboard.writeText(apiKey); toast.success('Copied'); }} className="btn-ghost btn-sm"><Copy size={13} /></button>
      </div>
      <pre className="mt-3 p-3 bg-ink-900 text-mint-300 rounded-xl text-[10px] overflow-x-auto">
{`POST /api/purchases/api
x-api-key: ${show ? apiKey : 'YOUR_KEY'}
{ "user": "username", "amount": 100, "currency": "RON" }`}
      </pre>
      <button onClick={() => confirm('Rotate API key?') && rotate.mutate()}
        className="btn-ghost btn-sm mt-2 text-red-600"><Key size={12} /> Rotate key</button>
    </div>
  );
}